import { LitElement, html } from 'lit';
import { TagKey } from '@cof/tag-catalog-fs-personal-mfe';
import { DebugService } from '../../../../shared/services/debug.service.js';
import { ApplicationService } from '../../../../services/application.service.js';
import { style } from './dob.style.js';
import { globalStyle } from '../../../../global-styles.style.js';
import { stratum } from '../../../../../constants/stratum.js';

class RefiPersonalDOBComponent extends LitElement {
  constructor() {
    super();
    this.debug = new DebugService('RefiStepIdentity');
    this.values = {};
    this.coAppPrefix = '';
    this.dobId = '';
    this.iconicV2 = ApplicationService.isIconicV2;
    this.debug.log('Iconic v2', this.iconicV2);
    this.errorMessage = '';
    this.touchedField = false;
    this.values.maskedValue = '';
  }

  static styles = [style, globalStyle];

  static properties = {
    applicant: { type: Object, reflect: true },
    isPrimary: { type: Boolean, attribute: 'is-primary', reflect: true },
    invalidAge: { type: Boolean, reflect: true },
    dateOfBirth: { type: String, reflect: true },
    invalidDate: { type: Boolean, reflect: true },
    errorMessage: { type: String, reflect: true },
  };

  connectedCallback() {
    super.connectedCallback();
    this.coAppPrefix = this.isPrimary ? '' : 'co-';
    this.dobId = `${this.coAppPrefix}dateOfBirth`;
    this.application = ApplicationService.Application;
    this.values.dateOfBirth = this.isPrimary
      ? this.application.primaryApplicant.dateOfBirth
      : this.application.coApplicant.dateOfBirth;
  }

  firstUpdated() {
    if (this.checkMaskPrefill()) {
      this.maskDob();
    }
  }

  resetValues() {
    this.application = ApplicationService.Application;
    this.values.dateOfBirth = this.isPrimary
      ? this.application.primaryApplicant.dateOfBirth
      : this.application.coApplicant.dateOfBirth;

    this.touchedField = false;
    // Ensure DOM is settled before grabbing the element
    setTimeout(() => {
      const dobField = this.shadowRoot.getElementById(this.dobId);
      this.dispatchEvent(
        new CustomEvent('validity', {
          detail: {
            name: 'dateOfBirth',
            isValid: dobField.validity.valid && dobField.value !== '',
          },
        })
      );

      this.errorMessage = '';
      if (this.checkMaskPrefill()) {
        this.maskDob();
      } else {
        dobField.setAttribute('value', this.values.dateOfBirth);
      }
      this.requestUpdate();
    }, 0);
  }

  updated(changedProperties) {
    super.updated(changedProperties);

    if (changedProperties.has('dateOfBirth')) {
      this.autoFormatDate();
    }
  }

  autoFormatDate() {
    const inputValue = this.values.dateOfBirth.replace('/[^0-9/]/g', '');
    let inputExcludingExtraSlash = inputValue;
    for (let i = 0; i < inputValue.length; i += 1) {
      if (i !== 2 && i !== 5 && inputValue[i] === '/') {
        inputExcludingExtraSlash =
          inputValue.slice(0, i) + inputValue.slice(i + 1);
      }
    }

    if (
      inputExcludingExtraSlash.length > 2 &&
      inputExcludingExtraSlash[2] !== '/'
    ) {
      inputExcludingExtraSlash = `${inputExcludingExtraSlash.slice(
        0,
        2
      )}/${inputExcludingExtraSlash.slice(2)}`;
    }
    if (
      inputExcludingExtraSlash.length > 5 &&
      inputExcludingExtraSlash[5] !== '/'
    ) {
      inputExcludingExtraSlash = `${inputExcludingExtraSlash.slice(
        0,
        5
      )}/${inputExcludingExtraSlash.slice(5)}`;
    }
    this.values.dateOfBirth = inputExcludingExtraSlash;
  }

  checkMaskPrefill() {
    // DOB needs to be masked (eg 01/**/2000) if the value has been prefilled and the user has not changed it
    if (this.iconicV2 && this.application.isPrefilled && !this.touchedField) {
      if (
        (this.isPrimary && !ApplicationService.getPrimaryApplicantDobChanged) ||
        (!this.isPrimary && !ApplicationService.getCoApplicantDobChanged)
      ) {
        return true;
      }
    }
    return false;
  }

  renderIconicV2() {
    return html`
      <div>
        <div class="personal__dob">
          <fs-text-field
            class="includes-error-message"
            id="${this.dobId}"
            autocomplete="bday"
            name="dob"
            type="text"
            value-missing-message="Please enter a valid date of birth and use MM/DD/YYYY format"
            error-message=${this.errorMessage}
            maxLength="10"
            pattern="(?:0[1-9]|1[012])/?(?:0[1-9]|[12]\\d|3[01]|(\\*\\*))/?(?:19|20)\\d\\d"
            required
            placeholder="${this.iconicV2 ? 'MM/DD/YYYY' : ''}"
            @focus="${(ev) => {
              // Clear DOB if in iconic v2 experiment and user has not touched the field
              if (this.checkMaskPrefill()) {
                const dobField = this.shadowRoot.getElementById(this.dobId);
                dobField.setAttribute('value', '');
                this.values.dateOfBirth = '';
                this.changed('dateOfBirth', ev);
              }
            }}"
            @change="${(ev) => {
              this.touchedField = true;

              this.values.dateOfBirth = ev.target.value;
              this.changed('dateOfBirth', ev);
            }}"
            @blur="${(ev) => {
              let parts;
              if (/^\d{8}$/.test(ev.target.value.trim())) {
                parts = [
                  ev.target.value.substring(0, 2).padStart(2, '0'),
                  ev.target.value.substring(2, 4).padStart(2, '0'),
                  ev.target.value.substring(4).padStart(4, '0'),
                ];
              } else {
                parts = ev.target.value.split('/');
              }
              this.invalidDateCheck(
                ev,
                parseInt(parts[0], 10),
                parseInt(parts[1], 10),
                parseInt(parts[2], 10)
              );
              this.invalidAgeCheck();
              if (ev.target.validity.valid) {
                stratum.publishTag(TagKey.PQ_TEXT_INPUT_FIELD_EDITED_FIELD, {
                  replacements: { value: this.dobId },
                });
              } else {
                stratum.publishTag(
                  TagKey.PQ_TEXT_INPUT_FIELD_FIELD_VALIDATION_ERROR_MESSAGE,
                  {
                    replacements: {
                      value:
                        'Please enter a valid date of birth and use MM/DD/YYYY format',
                    },
                  }
                );
              }
            }}"
            value="${this.checkMaskPrefill()
              ? this.values.maskedValue
              : this.values.dateOfBirth}"
            pattern-mismatch-message="Please enter a valid date of birth and use MM/DD/YYYY format"
          >
            Date of birth
          </fs-text-field>
        </div>
      </div>
    `;
  }

  render() {
    if (this.iconicV2) {
      return this.renderIconicV2();
    }
    return html`
      <div>
        <div class="personal__dob">
          <fs-text-field
            class="includes-error-message"
            id="${this.dobId}"
            autocomplete="bday"
            name="dob"
            type="text"
            value-missing-message="Please enter a valid date of birth and use MM/DD/YYYY format"
            maxLength="10"
            pattern="(?:0[1-9]|1[012])/?(?:0[1-9]|[12]\\d|3[01])/?(?:19|20)\\d\\d"
            required
            @change="${(ev) => {
              this.values.dateOfBirth = ev.target.value;
              this.changed('dateOfBirth', ev);
            }}"
            @blur="${(ev) => {
              this.invalidAgeCheck();
              const parts = ev.target.value.split('/');
              this.invalidDateCheck(
                ev,
                parseInt(parts[0], 10),
                parseInt(parts[1], 10),
                parseInt(parts[2], 10)
              );
              if (ev.target.validity.valid) {
                stratum.publishTag(TagKey.PQ_TEXT_INPUT_FIELD_EDITED_FIELD, {
                  replacements: { value: this.dobId },
                });
              } else {
                stratum.publishTag(
                  TagKey.PQ_TEXT_INPUT_FIELD_FIELD_VALIDATION_ERROR_MESSAGE,
                  {
                    replacements: {
                      value:
                        'Please enter a valid date of birth and use MM/DD/YYYY format',
                    },
                  }
                );
              }
            }}"
            value="${this.values.dateOfBirth}"
            pattern-mismatch-message="Please enter a valid date of birth and use MM/DD/YYYY format"
          >
            Date of birth
          </fs-text-field>
          <fs-body-text
            size="small"
            variant="feedback-negative"
            ?hidden="${!this.invalidAge && !this.invalidDate}"
            id="${`${this.dobId}-invalidAge`}"
          >
            ${this.invalidDate
              ? html`Please enter a valid date of birth and use MM/DD/YYYY
                format`
              : html`Applicant must be at least 18 years of age`}
          </fs-body-text>
        </div>
      </div>
    `;
  }

  formatDob(month, day, year) {
    // Check if the value is 8 digits and format to MM/DD/YYYY format
    this.debug.log('DOB value initially', this.values.dateOfBirth);
    const monthStr = month.toString().padStart(2, '0');
    const dayStr = day.toString().padStart(2, '0');
    const yearStr = year.toString().padStart(4, '0');

    const formattedDob = `${monthStr}/${dayStr}/${yearStr}`;

    this.values.dateOfBirth = formattedDob;
    this.requestUpdate();
    this.debug.log('DOB value formatted to', this.values.dateOfBirth);
  }

  maskDob() {
    // HTML attribute value is masked. True value for business validation checking is in this.values
    if (this.values.dateOfBirth) {
      let dob = this.values.dateOfBirth;
      const dobField = this.shadowRoot.getElementById(this.dobId);
      dob = `${dob.substring(0, 3)}**${dob.substring(5)}`;

      this.values.maskedValue = dob;
      dobField.setAttribute('value', this.values.maskedValue);
      this.requestUpdate();
    }
  }

  updateDobChangedTag() {
    // DOB has changed only if the user has touched it
    if (this.isPrimary && this.touchedField) {
      ApplicationService.primaryApplicantDobChanged = true;
    } else if (!this.isPrimary && this.touchedField) {
      ApplicationService.coApplicantDobChanged = true;
    }
  }

  changed(name, ev) {
    if (ev.target.validity.valid) {
      this.values[name] = ev.target.value;
    }
    this.dispatchEvent(
      new CustomEvent('validity', {
        detail: { name, isValid: ev.target.validity.valid },
      })
    );
  }

  runBusinessChecks() {
    this.debug.log('Checking', this.values.dateOfBirth);
    const parts = this.values.dateOfBirth.split('/');
    this.invalidDateCheck(
      null,
      parseInt(parts[0], 10),
      parseInt(parts[1], 10),
      parseInt(parts[2], 10)
    );
    this.invalidAgeCheck();
  }

  invalidAgeCheck() {
    if (
      Number.isNaN(new Date(this.values.dateOfBirth)) ||
      this.values.dateOfBirth === ''
    ) {
      this.invalidAge = false;
      return;
    }
    this.invalidAge = !this.isAtLeast18YearsOld(this.values.dateOfBirth);
    if (this.invalidAge && !this.invalidDate) {
      stratum.publishTag(
        TagKey.PQ_TEXT_INPUT_FIELD_FIELD_VALIDATION_ERROR_MESSAGE,
        {
          replacements: { value: 'Applicant must be at least 18 years of age' },
        }
      );
      this.errorMessage = 'Applicant must be at least 18 years of age';
    }
    if (!this.invalidDate && !this.invalidAge) {
      this.errorMessage = '';
    }
    this.dispatchEvent(
      new CustomEvent('validity', {
        detail: {
          name: 'invalidAge',
          isValid: !this.invalidAge,
        },
      })
    );
  }

  invalidDateCheck(ev, month, day, year) {
    this.debug.log(
      `Checking invalid date for month ${month} day ${day} year ${year}`
    );
    const validity = !(
      month > 12 ||
      (this.iconicV2 && Number.isNaN(day)) ||
      day > 31 ||
      day <= 0 ||
      month <= 0 ||
      (day > 30 && [4, 6, 9, 11].includes(month)) ||
      (year % 4 === 0 && month === 2 && day > 29) ||
      (year % 4 !== 0 && month === 2 && day > 28)
    );
    this.invalidDate = !validity;
    if (this.invalidDate) {
      this.errorMessage =
        'Please enter a valid date of birth and use MM/DD/YYYY format';
    }
    if (!this.invalidDate && !this.invalidAge) {
      this.errorMessage = '';
      if (this.iconicV2) {
        this.formatDob(month, day, year);
      }
    }

    this.dispatchEvent(
      new CustomEvent('validity', {
        detail: { name: 'dateOfBirth', isValid: validity },
      })
    );
    this.dispatchEvent(
      new CustomEvent('validity', {
        detail: { name: 'invalid-date', isValid: validity },
      })
    );
  }

  isAtLeast18YearsOld(dob) {
    const currentDate = new Date();
    const eighteenYearsAgo = new Date(
      currentDate.getFullYear() - 18,
      currentDate.getMonth(),
      currentDate.getDate()
    );
    return new Date(dob) <= eighteenYearsAgo;
  }
}

customElements.define('refi-personal-dob', RefiPersonalDOBComponent);
